/**
 * BarBandPrimitive — a per-bar band (upper/lower) FILLED across each bar's own width.
 *
 * The companion of BarLevelPrimitive: that one strokes a level across the bar it
 * governs; this one fills between two such levels, so a fill and its edge lines
 * sit in exactly the same register. BandPrimitive interpolates between points,
 * which for a per-bar level puts half of every bar under its neighbour's value —
 * the half-bar offset BarLevelPrimitive was written to remove.
 *
 * Each point may carry its own colour (e.g. by the band's direction); the fill
 * of consecutive bars joins on their shared boundary.
 */
import type {
  ISeriesPrimitive, IPrimitivePaneView, IPrimitivePaneRenderer, SeriesAttachedParameter,
  Time, IChartApi, ISeriesApi, SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface BarBandPoint { time: number; upper: number; lower: number; color: string }

class BarBandRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly p: BarBandPrimitive) {}

  draw(target: CanvasRenderingTarget2D): void {
    const { chart, series, points } = this.p;
    if (!chart || !series || points.length === 0) return;
    const ts = chart.timeScale();
    let spacing = 6;
    try { spacing = Number((ts.options() as any).barSpacing) || 6; } catch { /* default */ }
    const half = spacing / 2;
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      for (const pt of points) {
        const xc = ts.timeToCoordinate(pt.time as Time);
        const yu = series.priceToCoordinate(pt.upper);
        const yl = series.priceToCoordinate(pt.lower);
        if (xc == null || yu == null || yl == null) continue;
        // Round both edges to whole bitmap pixels so neighbouring bars meet with
        // no hairline gap and no overlap (overlap would double the alpha).
        const xl = Math.round((Number(xc) - half) * hpr);
        const xr = Math.round((Number(xc) + half) * hpr);
        if (xr < 0 || xl > bitmapSize.width || xr <= xl) continue;
        const top = Math.round(Math.min(Number(yu), Number(yl)) * vpr);
        const bot = Math.round(Math.max(Number(yu), Number(yl)) * vpr);
        ctx.fillStyle = pt.color;
        ctx.fillRect(xl, top, xr - xl, Math.max(1, bot - top));
      }
      ctx.restore();
    });
  }
}

class BarBandPaneView implements IPrimitivePaneView {
  constructor(private readonly p: BarBandPrimitive) {}
  /** Behind the candles: it is a wash, the candles are the subject. */
  zOrder() { return 'bottom' as const; }
  renderer(): IPrimitivePaneRenderer | null { return new BarBandRenderer(this.p); }
}

export class BarBandPrimitive implements ISeriesPrimitive<Time> {
  points: BarBandPoint[];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _views: BarBandPaneView[];

  constructor(points: BarBandPoint[]) {
    this.points = points;
    this._views = [new BarBandPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }
  updateAllViews(): void { /* the renderer reads this.points directly */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._views; }
}
